import { useCallback, useEffect, useMemo, useState } from "react";
import { FileAudio, Loader2, Pause, Play, Search, Upload, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { Chip, ChipGroup, Field, inputCls } from "./Chips";
import { Cover } from "./Cover";
import { LyricsEditor } from "./LyricsEditor";
import { usePlayer } from "../context/PlayerContext";
import { errMsg, getAuthorName, http, setAuthorName } from "../lib/api";
import { buildStyle, GENRES, MOODS, VOICES } from "../lib/presets";

const SourcePicker = ({ songs, value, onChange }) => {
  const [q, setQ] = useState("");
  const { current, playing, play } = usePlayer();
  const shown = songs.filter((s) => `${s.title} ${s.author_name}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)] overflow-hidden">
      <div className="relative border-b border-[var(--c-line)]">
        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input data-testid="cover-source-search" value={q} onChange={(e) => setQ(e.target.value)}
          className="w-full bg-transparent pl-10 pr-4 py-3 text-sm focus:outline-none" placeholder="Ieškoti dainos..." />
      </div>
      <div className="max-h-64 overflow-auto scroll-thin p-2 space-y-1">
        {shown.length === 0 && <p className="text-sm text-zinc-500 p-4">Dainų nerasta.</p>}
        {shown.map((s) => (
          <div key={s.id} data-testid={`cover-source-${s.id}`} onClick={() => onChange(s)}
            className={`flex items-center gap-3 p-2 rounded-xl cursor-pointer transition-colors ${value?.id === s.id ? "bg-[var(--c-orange)]/15 ring-1 ring-[var(--c-orange)]" : "hover:bg-[var(--c-hover)]"}`}>
            <Cover song={s} className="w-10 h-10 rounded-lg shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{s.title}</p>
              <p className="text-xs text-zinc-500 truncate">{s.author_name}</p>
            </div>
            <button data-testid={`cover-source-play-${s.id}`} onClick={(e) => { e.stopPropagation(); play(s, [s]); }}
              className="p-2 rounded-full hover:bg-white/10 text-zinc-300">
              {current?.id === s.id && playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const UploadBox = ({ upload, setUpload }) => {
  const [pct, setPct] = useState(0);
  const pick = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Failas per didelis (daugiausia 25 MB)");
    const fd = new FormData();
    fd.append("file", file);
    setUpload({ name: file.name, path: "" });
    try {
      const { data } = await http.post("/uploads/audio", fd, { onUploadProgress: (e) => setPct(Math.round((e.loaded / (e.total || 1)) * 100)) });
      setUpload({ name: file.name, path: data.path });
      toast.success("Failas įkeltas");
    } catch (e) {
      setUpload(null);
      toast.error(errMsg(e));
    }
  };
  return (
    <label data-testid="cover-upload-dropzone" className="flex flex-col items-center justify-center gap-2 p-8 rounded-2xl border-2 border-dashed border-[var(--c-line)] hover:border-[var(--c-orange)] bg-[var(--c-surface)] cursor-pointer transition-colors text-center">
      <input data-testid="cover-upload-input" type="file" accept=".mp3,.wav,.flac,audio/mpeg,audio/wav,audio/flac" className="hidden" onChange={(e) => pick(e.target.files[0])} />
      {upload ? (
        <>
          {upload.path ? <FileAudio className="w-8 h-8 text-[var(--c-orange)]" /> : <Loader2 className="w-8 h-8 animate-spin text-[var(--c-violet)]" />}
          <p data-testid="cover-upload-name" className="text-sm font-semibold">{upload.name}</p>
          <p className="text-xs text-zinc-500">{upload.path ? "Įkelta · spustelėkite, jei norite pakeisti" : `Įkeliama… ${pct}%`}</p>
        </>
      ) : (
        <>
          <Upload className="w-8 h-8 text-zinc-500" />
          <p className="text-sm font-semibold">Įkelkite savo dainą</p>
          <p className="text-xs text-zinc-500">MP3, WAV arba FLAC · iki 25 MB</p>
        </>
      )}
    </label>
  );
};

const validateCover = (src, picked, upload, style) => {
  if (src === "site" && !picked) return "Pasirinkite dainą";
  if (src === "upload" && !upload?.path) return "Įkelkite garso failą";
  if (style.length < 3) return "Pasirinkite naują žanrą arba aprašykite stilių";
  return "";
};

const coverPayload = (src, picked, upload, f, style) => ({
  source_song_id: src === "site" ? picked.id : null, upload_path: src === "upload" ? upload.path : null,
  title: f.title, style, genre: f.genre, source_style: f.srcStyle, source_lyrics: f.srcLyrics,
  lyrics: f.changeLyrics ? f.lyrics : "", instrumental: f.instrumental, closeness: f.closeness,
  author_name: f.author.trim() || "Anonimas", is_public: f.isPublic,
});

const useCoverSources = (sourceId) => {
  const [songs, setSongs] = useState([]);
  const [picked, setPicked] = useState(null);
  useEffect(() => {
    Promise.all([http.get("/songs/mine"), http.get("/discover", { params: { sort: "new" } })]).then(([a, b]) => {
      const all = [...a.data.filter((s) => s.status === "completed"), ...b.data];
      const uniq = [...new Map(all.map((s) => [s.id, s])).values()];
      setSongs(uniq);
      const pre = uniq.find((s) => s.id === sourceId);
      if (pre) setPicked(pre);
    });
  }, [sourceId]);
  return { songs, picked, setPicked };
};

const UploadFields = ({ f, set }) => (
  <div className="grid gap-6">
    <Field label="Originalo stilius (nebūtina)">
      <input data-testid="cover-source-style-input" value={f.srcStyle} onChange={(e) => set("srcStyle")(e.target.value)} className={inputCls} placeholder="pvz.: acoustic folk, female vocals" />
    </Field>
    <Field label="Originalo žodžiai (nebūtina, pagerina rezultatą)">
      <textarea data-testid="cover-source-lyrics-input" rows={4} value={f.srcLyrics} onChange={(e) => set("srcLyrics")(e.target.value)} className={`${inputCls} font-mono`} />
    </Field>
  </div>
);

const ClosenessField = ({ value, onChange }) => {
  const v = useMemo(() => [value], [value]);
  return (
    <Field label="Cover stiprumas" right={<span data-testid="cover-closeness-value" className="text-xs font-mono text-[var(--c-orange)]">{value}% artumas originalui</span>}>
      <div className="p-4 rounded-2xl border border-[var(--c-line)] bg-[var(--c-surface)]">
        <Slider data-testid="cover-closeness-slider" value={v} min={0} max={100} step={5} onValueChange={([x]) => onChange(x)} />
        <div className="flex justify-between text-[11px] text-zinc-500 mt-3">
          <span>Laisva interpretacija – daugiau naujo stiliaus</span><span>Artima originalui</span>
        </div>
      </div>
    </Field>
  );
};

const SW = ({ testId, checked, onChange, children }) => (
  <label className="flex items-center gap-2.5 cursor-pointer"><Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />{children}</label>
);

const CoverToggles = ({ f, set }) => (
  <div className="flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-zinc-300">
    <SW testId="cover-lyrics-switch" checked={f.changeLyrics} onChange={set("changeLyrics")}>Keisti žodžius</SW>
    <SW testId="cover-instrumental-switch" checked={f.instrumental} onChange={set("instrumental")}>Instrumentinė</SW>
    <SW testId="cover-public-switch" checked={f.isPublic} onChange={set("isPublic")}>Rodyti „Atrasti“</SW>
  </div>
);

const useCoverForm = (sourceId, onCreated) => {
  const [src, setSrc] = useState("site");
  const { songs, picked, setPicked } = useCoverSources(sourceId);
  const [upload, setUpload] = useState(null);
  const [f, setF] = useState({ title: "", genre: "", moods: [], voice: "", extra: "", srcStyle: "", srcLyrics: "",
    instrumental: false, closeness: 60, changeLyrics: false, lyrics: "", author: getAuthorName(), isPublic: true });
  const [busy, setBusy] = useState(false);
  const set = useCallback((k) => (v) => setF((s) => ({ ...s, [k]: v })), []);
  const baseLyrics = useMemo(() => (src === "site" ? picked?.lyrics : f.srcLyrics) || "", [src, picked, f.srcLyrics]);
  useEffect(() => { setF((s) => ({ ...s, lyrics: baseLyrics })); }, [baseLyrics]);
  const style = buildStyle(f.genre, f.moods, f.voice, f.extra);

  const submit = async () => {
    const problem = validateCover(src, picked, upload, style);
    if (problem) return toast.error(problem);
    setBusy(true);
    setAuthorName(f.author);
    try {
      await http.post("/songs/remake", coverPayload(src, picked, upload, f, style));
      toast.success("Kuriama cover versija! Tai užtruks ~1–2 min.");
      onCreated();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { src, setSrc, songs, picked, setPicked, upload, setUpload, f, set, busy, style, submit };
};

const SourceField = ({ c }) => (
  <Field label="Originali daina">
    <div className="flex gap-2 mb-3">
      <Chip testId="cover-src-site" active={c.src === "site"} onClick={() => c.setSrc("site")}>Iš svetainės</Chip>
      <Chip testId="cover-src-upload" active={c.src === "upload"} onClick={() => c.setSrc("upload")}>Įkelti failą</Chip>
    </div>
    {c.src === "site" ? <SourcePicker songs={c.songs} value={c.picked} onChange={c.setPicked} /> : <UploadBox upload={c.upload} setUpload={c.setUpload} />}
  </Field>
);

const StyleFields = ({ f, set }) => (
  <>
    <ChipGroup label="Naujas žanras" items={GENRES} value={f.genre} onChange={set("genre")} prefix="cover-genre-chip" />
    <ChipGroup label="Nuotaika" items={MOODS} value={f.moods} onChange={set("moods")} prefix="cover-mood-chip" color="violet" multi />
    {!f.instrumental && <ChipGroup label="Vokalas" items={VOICES} value={f.voice} onChange={set("voice")} prefix="cover-voice-chip" color="violet" />}
    <ClosenessField value={f.closeness} onChange={set("closeness")} />
    <Field label="Papildomas stilius">
      <input data-testid="cover-style-input" value={f.extra} onChange={(e) => set("extra")(e.target.value)} className={inputCls} placeholder="pvz.: heavy guitars, 140 bpm, live drums" />
    </Field>
  </>
);

const MetaFields = ({ c }) => (
  <div className="grid sm:grid-cols-2 gap-6">
    <Field label="Pavadinimas">
      <input data-testid="cover-title-input" maxLength={80} value={c.f.title} onChange={(e) => c.set("title")(e.target.value)} className={inputCls}
        placeholder={c.picked && c.src === "site" ? `${c.picked.title} (cover)` : "Cover pavadinimas"} />
    </Field>
    <Field label="Autorius">
      <input data-testid="cover-author-input" maxLength={40} value={c.f.author} onChange={(e) => c.set("author")(e.target.value)} className={inputCls} placeholder="Jūsų vardas" />
    </Field>
  </div>
);

export const CoverForm = ({ sourceId, onCreated }) => {
  const c = useCoverForm(sourceId, onCreated);
  const { f, set } = c;
  return (
    <div data-testid="cover-form" className="space-y-8">
      <p className="text-sm text-zinc-400 -mt-2">Išlaikoma originalo melodija ir struktūra, bet daina perdainuojama nauju stiliumi. Žodžius galite pakeisti.</p>
      <SourceField c={c} />
      {c.src === "upload" && <UploadFields f={f} set={set} />}
      <StyleFields f={f} set={set} />
      {f.changeLyrics && !f.instrumental && (
        <Field label="Nauji žodžiai"><LyricsEditor value={f.lyrics} onChange={set("lyrics")} idea={f.title || c.picked?.title} style={c.style} /></Field>
      )}
      <MetaFields c={c} />
      <CoverToggles f={f} set={set} />
      <button data-testid="cover-submit-button" onClick={c.submit} disabled={c.busy}
        className="w-full sm:w-auto flex items-center justify-center gap-2.5 px-10 py-4 rounded-full bg-[var(--c-orange)] text-white font-display font-semibold glow-orange hover:bg-[#ff7a26] active:scale-[0.98] transition-[background-color,transform] duration-200 disabled:opacity-60">
        {c.busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}Kurti cover versiją
      </button>
    </div>
  );
};
